import { useState, useRef, useEffect } from 'react';
import { Flag, X, Edit2, ChevronRight } from 'lucide-react';
import { useStudioStore } from '../../store/useStudioStore';

interface MarkerTrackProps {
  pixelsPerBeat?: number;
  totalBeats?: number;
  currentTime?: number; // in beats
  onSeek?: (time: number) => void;
}

export const MarkerTrack: React.FC<MarkerTrackProps> = ({
  pixelsPerBeat = 100,
  totalBeats = 64,
  currentTime = 0,
  onSeek,
}) => {
  const { markers, addMarker, removeMarker, updateMarker } = useStudioStore();
  const [editingMarkerId, setEditingMarkerId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [showMarkerList, setShowMarkerList] = useState(false);
  const trackRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus input when editing starts
  useEffect(() => {
    if (editingMarkerId && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editingMarkerId]);

  // Handle keyboard shortcuts (Cmd+1-9 to jump to markers)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key >= '1' && e.key <= '9') {
        e.preventDefault();
        const index = parseInt(e.key) - 1;
        if (index < markers.length && onSeek) {
          onSeek(markers[index].time);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [markers, onSeek]);

  const handleTrackClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!trackRef.current) return;
    
    const rect = trackRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const time = x / pixelsPerBeat;
    
    // Check if clicking near existing marker (within 10px)
    const clickedMarker = markers.find(m => {
      const markerX = m.time * pixelsPerBeat;
      return Math.abs(markerX - x) < 10;
    });
    
    if (clickedMarker) {
      // Jump to marker
      if (onSeek) {
        onSeek(clickedMarker.time);
      }
    } else {
      // Create new marker
      const markerNumber = markers.length + 1;
      addMarker({
        time: Math.max(0, Math.min(time, totalBeats)),
        name: `Marker ${markerNumber}`,
        color: getMarkerColor(markerNumber),
      });
    }
  };

  const handleMarkerNameSubmit = () => {
    if (editingMarkerId && editingName.trim()) {
      updateMarker(editingMarkerId, { name: editingName.trim() });
    }
    setEditingMarkerId(null);
    setEditingName('');
  };

  const handleMarkerNameKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleMarkerNameSubmit();
    } else if (e.key === 'Escape') {
      setEditingMarkerId(null);
      setEditingName('');
    }
  };

  const startEditingMarker = (marker: typeof markers[0], e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingMarkerId(marker.id);
    setEditingName(marker.name);
  };

  const handleDeleteMarker = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    removeMarker(id);
  };

  const handleMarkerClick = (marker: typeof markers[0], e: React.MouseEvent) => {
    e.stopPropagation();
    if (onSeek) {
      onSeek(marker.time);
    }
  };

  return (
    <div className="flex flex-col">
      {/* Marker Track Lane */}
      <div className="relative h-12 bg-[#1a1a1a] border-b border-[#2a2a2a]">
        {/* Track Label */}
        <div className="absolute left-0 top-0 h-full w-32 bg-[#1f1f1f] border-r border-[#2a2a2a] flex items-center justify-between px-3">
          <div className="flex items-center gap-2">
            <Flag size={14} className="text-[#6366f1]" />
            <span className="text-xs font-medium">Markers</span>
          </div>
          <button
            onClick={() => setShowMarkerList(!showMarkerList)}
            className="p-1 hover:bg-[#2a2a2a] rounded transition-colors"
            title="Toggle marker list"
          >
            <ChevronRight
              size={12}
              className={`transition-transform ${showMarkerList ? 'rotate-90' : ''}`}
            />
          </button>
        </div>

        {/* Marker Lane */}
        <div
          ref={trackRef}
          className="absolute left-32 right-0 top-0 h-full cursor-crosshair"
          onClick={handleTrackClick}
          style={{ width: `${totalBeats * pixelsPerBeat}px` }}
        >
          {/* Grid lines (every 4 beats) */}
          {Array.from({ length: Math.ceil(totalBeats / 4) }).map((_, i) => (
            <div
              key={i}
              className="absolute top-0 bottom-0 w-px bg-[#2a2a2a]"
              style={{ left: `${i * 4 * pixelsPerBeat}px` }}
            />
          ))}

          {/* Markers */}
          {markers.map((marker, index) => (
            <div
              key={marker.id}
              className="absolute top-0 bottom-0 group"
              style={{ left: `${marker.time * pixelsPerBeat}px` }}
            >
              {/* Marker Flag */}
              <div
                className="relative cursor-pointer"
                onClick={(e) => handleMarkerClick(marker, e)}
              >
                {/* Flag pole */}
                <div
                  className="absolute w-0.5 h-full"
                  style={{ backgroundColor: marker.color }}
                />
                
                {/* Flag */}
                <div
                  className="absolute top-0 left-0.5 px-2 py-1 rounded-r text-xs font-medium whitespace-nowrap shadow-lg transition-all group-hover:shadow-xl"
                  style={{
                    backgroundColor: marker.color,
                    color: '#ffffff',
                  }}
                >
                  {editingMarkerId === marker.id ? (
                    <input
                      ref={inputRef}
                      type="text"
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      onBlur={handleMarkerNameSubmit}
                      onKeyDown={handleMarkerNameKeyDown}
                      className="bg-white/20 px-1 rounded outline-none w-32"
                      onClick={(e) => e.stopPropagation()}
                    />
                  ) : (
                    <div className="flex items-center gap-1">
                      <span>{marker.name}</span>
                      <span className="opacity-50 text-[10px]">
                        (Cmd+{index + 1})
                      </span>
                    </div>
                  )}
                </div>

                {/* Hover actions */}
                <div className="absolute top-0 left-0.5 mt-8 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                  <button
                    onClick={(e) => startEditingMarker(marker, e)}
                    className="p-1 bg-[#2a2a2a] hover:bg-[#3a3a3a] rounded"
                    title="Rename marker"
                  >
                    <Edit2 size={10} />
                  </button>
                  <button
                    onClick={(e) => handleDeleteMarker(marker.id, e)}
                    className="p-1 bg-[#2a2a2a] hover:bg-red-600 rounded"
                    title="Delete marker"
                  >
                    <X size={10} />
                  </button>
                </div>
              </div>
            </div>
          ))}

          {/* Playhead indicator */}
          {currentTime !== undefined && (
            <div
              className="absolute top-0 bottom-0 w-0.5 bg-white pointer-events-none"
              style={{ left: `${currentTime * pixelsPerBeat}px` }}
            />
          )}
        </div>
      </div>

      {/* Marker List Sidebar */}
      {showMarkerList && (
        <div className="bg-[#1f1f1f] border-b border-[#2a2a2a] p-3">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-xs font-semibold">Marker List</h4>
            <span className="text-[10px] text-zinc-500">
              {markers.length} marker{markers.length !== 1 ? 's' : ''}
            </span>
          </div>
          
          {markers.length === 0 ? (
            <p className="text-xs text-zinc-500 italic">
              Click on the marker track to create markers
            </p>
          ) : (
            <div className="space-y-1 max-h-40 overflow-y-auto">
              {markers.map((marker, index) => (
                <div
                  key={marker.id}
                  className="flex items-center gap-2 p-2 hover:bg-[#2a2a2a] rounded cursor-pointer group"
                  onClick={() => onSeek && onSeek(marker.time)}
                >
                  <div
                    className="w-3 h-3 rounded-sm flex-shrink-0"
                    style={{ backgroundColor: marker.color }}
                  />
                  <span className="text-xs flex-1 truncate">{marker.name}</span>
                  <span className="text-[10px] text-zinc-500">
                    {marker.time.toFixed(1)}
                  </span>
                  <span className="text-[10px] text-zinc-500 opacity-50">
                    Cmd+{index + 1}
                  </span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      removeMarker(marker.id);
                    }}
                    className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-600 rounded transition-all"
                  >
                    <X size={10} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Helper function to get marker color based on index
function getMarkerColor(index: number): string {
  const colors = [
    '#6366f1', // indigo
    '#8b5cf6', // purple
    '#ec4899', // pink
    '#f59e0b', // amber
    '#10b981', // emerald
    '#3b82f6', // blue
    '#ef4444', // red
    '#06b6d4', // cyan
  ];
  return colors[(index - 1) % colors.length];
}
